import React from "react";
import { SxProps, Typography } from "@mui/material";
import StaticIcon from "../../../Components/StaticIcon";
import useControlAttribute from "../../../hooks/useControlAttribute";
import "./PhotoVoltaicSystem.css";
import { IControlPhotoVoltaic, TPhotoVoltaicValueAttribute } from "../types";
import FlowIndicator, { TOrientation, TDirection } from "./FlowIndicator";

const Value = (props: {
  control: IControlPhotoVoltaic;
  attributeName: TPhotoVoltaicValueAttribute;
  unit: string;
  digits?: number;
  sx?: SxProps;
}) => {
  const { control, attributeName, unit, digits, sx } = props;
  const value = useControlAttribute(control, attributeName);
  const sxProps = sx || { fontSize: 14, fontWeight: "bold" };
  return (
    <Typography sx={{ lineHeight: 1, ...sxProps }}>
      {value.toFixed(digits || 0)}
      {unit}
    </Typography>
  );
};

const EnergyFlow = (props: {
  control: IControlPhotoVoltaic;
  attributeName: TPhotoVoltaicValueAttribute;
  orientation: TOrientation;
  reverse?: boolean;
}) => {
  const { control, attributeName, orientation, reverse } = props;
  const value = useControlAttribute(control, attributeName);
  const toDirection = (value: number): TDirection => {
    if (reverse || false) {
      return value < 0 ? "forwards" : value > 0 ? "backwards" : "none";
    }
    return value > 0 ? "forwards" : value < 0 ? "backwards" : "none";
  };
  return (
    <FlowIndicator direction={toDirection(value)} orientation={orientation} />
  );
};

interface IPhotoVoltaicSystemProps {
  control: IControlPhotoVoltaic;
}

const PhotoVoltaicSystem = (props: IPhotoVoltaicSystemProps) => {
  const { control } = props;
  const iconStyle = { fontSize: 32, color: "white" };
  return (
    <div>
      <style>{"table{margin-left:auto; margin-right:auto;}"}</style>
      <style>{".space-below{padding-bottom:6px;}"}</style>
      <table cellSpacing={0} cellPadding={0}>
        <tbody>
          <tr>
            <td colSpan={5} className="space-below">
              <StaticIcon name="solar-power-variant-outline" sx={iconStyle} />
              <Value
                control={control}
                attributeName="power_pv"
                unit=" kW"
                digits={3}
              />
            </td>
          </tr>
          <tr>
            <td colSpan={5}>
              <EnergyFlow
                control={control}
                attributeName="power_pv"
                orientation="vertical"
              />
            </td>
          </tr>
          <tr>
            <td width="70px">
              <StaticIcon name="home-battery-outline" sx={iconStyle} />
            </td>
            <td>
              <EnergyFlow
                control={control}
                attributeName="power_bat"
                orientation="horizontal"
              />
            </td>
            <td width="70px">
              <StaticIcon name="home-outline" sx={iconStyle} />
            </td>
            <td>
              <EnergyFlow
                control={control}
                attributeName="power_grid"
                orientation="horizontal"
                reverse={true}
              />
            </td>
            <td width="70px">
              <StaticIcon name="transmission-tower" sx={iconStyle} />
            </td>
          </tr>
          <tr>
            <td>
              <Value
                control={control}
                attributeName="power_bat"
                unit="&nbsp;kW"
                digits={3}
              />
            </td>
            <td></td>
            <td>
              <Value
                control={control}
                attributeName="power_load"
                unit="&nbsp;kW"
                digits={3}
              />
            </td>
            <td></td>
            <td>
              <Value
                control={control}
                attributeName="power_grid"
                unit="&nbsp;kW"
                digits={3}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PhotoVoltaicSystem;
